import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api, { mediaSrc } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Brain, Plus, Search, FileText, Trash2, Upload, Sparkles, Pencil, Download, Paperclip, Loader2, User, Heart, Repeat } from 'lucide-react';

// Jev files free-text notes into one of these tidy buckets.
const NOTE_TYPES = {
  fact: { label: 'Facts', single: 'Fact', icon: User, color: 'hsl(var(--brand-mint))', hint: 'e.g. Works in fintech, based in Berlin, has a dog named Rex' },
  preference: { label: 'Preferences', single: 'Preference', icon: Heart, color: 'hsl(var(--brand-honey))', hint: 'e.g. Prefers concise answers; likes deep work in the morning' },
  routine: { label: 'Routines', single: 'Routine', icon: Repeat, color: 'hsl(var(--accent))', hint: 'e.g. Gym at 6am; team standup every Monday; weekly review on Fridays' },
};

export default function MemoryPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('fact');
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [form, setForm] = useState({ content: '', title: '', type: 'fact', tags: '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { const res = await api.get('/memory'); setItems(res.data); }, []);
  useEffect(() => { load(); }, [load]);

  const counts = items.reduce((acc, i) => { acc[i.type] = (acc[i.type] || 0) + 1; return acc; }, {});
  const filtered = items.filter((i) => i.type === tab && (!q || (i.content + ' ' + (i.title || '')).toLowerCase().includes(q.toLowerCase())));

  const openNew = () => { setForm({ content: '', title: '', type: tab === 'doc' ? 'fact' : tab, tags: '' }); setDialog({ open: true, item: null }); };
  const openEdit = (item) => { setForm({ content: item.content, title: item.title || '', type: item.type, tags: (item.tags || []).join(', ') }); setDialog({ open: true, item }); };

  const save = async () => {
    if (!form.content.trim()) { toast.error('Add some content'); return; }
    const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
    setSaving(true);
    try {
      if (dialog.item) {
        await api.put(`/memory/${dialog.item.memory_id}`, payload);
        toast.success('Memory saved');
        if (NOTE_TYPES[payload.type]) setTab(payload.type);
      } else {
        const res = await api.post('/memory', payload);
        const saved = res.data || {};
        if (saved.auto_tagged && saved.type !== form.type && NOTE_TYPES[saved.type]) {
          toast.success(`Jev filed this under ${NOTE_TYPES[saved.type].single}`, { description: 'Auto-sorted to keep your Universal Memory tidy.' });
        } else {
          toast.success('Memory saved');
        }
        if (NOTE_TYPES[saved.type]) setTab(saved.type);
      }
      setDialog({ open: false, item: null }); load();
    } catch (e) { toast.error('Could not save'); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/memory/${id}`); toast.success('Deleted'); load(); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { await api.post('/files?scope=memory', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Document added to memory'); load(); }
    catch (err) { toast.error('Upload failed'); }
  };

  const uploadAny = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File is over 25 MB'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { await api.post('/memory/attach', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('File attached to memory'); load(); }
    catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const meta = NOTE_TYPES[tab];
  const isDoc = tab === 'doc';

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-6 py-4 border-b border-[hsl(var(--border))]">
        <h1 className="font-display text-xl font-700 flex items-center gap-2"><Brain className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /> Universal Memory</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">What your agents remember about you — captured automatically, auto-sorted by Jev, and editable anytime.</p>
      </div>
      <div className="px-6 py-3 border-b border-[hsl(var(--border))] flex flex-col sm:flex-row gap-3 sm:items-center">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="fact" data-testid="memory-tab-facts">Facts{counts.fact ? ` (${counts.fact})` : ''}</TabsTrigger>
            <TabsTrigger value="preference" data-testid="memory-tab-preferences">Preferences{counts.preference ? ` (${counts.preference})` : ''}</TabsTrigger>
            <TabsTrigger value="routine" data-testid="memory-tab-routines">Routines{counts.routine ? ` (${counts.routine})` : ''}</TabsTrigger>
            <TabsTrigger value="doc" data-testid="memory-tab-docs">Documents{counts.doc ? ` (${counts.doc})` : ''}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-xs"><Search className="absolute left-3 top-2.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search memory" className="h-10 rounded-xl pl-9" data-testid="memory-search-input" /></div>
        <div className="flex gap-2 sm:ml-auto">
          {isDoc && <label className="inline-flex items-center gap-1.5 rounded-xl border border-[hsl(var(--border))] px-3 h-10 text-sm cursor-pointer hover:bg-[hsl(var(--secondary))] transition-colors"><Upload className="h-4 w-4" /> Upload .md<input type="file" accept=".md,.markdown,.txt" className="hidden" onChange={upload} data-testid="memory-upload-input" /></label>}
          {isDoc && <label className="inline-flex items-center gap-1.5 rounded-xl border border-[hsl(var(--border))] px-3 h-10 text-sm cursor-pointer hover:bg-[hsl(var(--secondary))] transition-colors" data-testid="memory-attach-label">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach file<input type="file" className="hidden" onChange={uploadAny} data-testid="memory-attach-input" /></label>}
          <Button className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} onClick={openNew} data-testid="add-memory-button"><Plus className="h-4 w-4" /> Add</Button>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-6">
          {filtered.length === 0 ? (
            <div className="text-center py-16"><div className="h-14 w-14 rounded-2xl mx-auto grid place-items-center mb-3" style={{ background: 'hsl(var(--accent))' }}><Sparkles className="h-7 w-7" style={{ color: 'hsl(var(--primary))' }} /></div><p className="font-600">Nothing here yet</p><p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-sm mx-auto">{isDoc ? 'Upload markdown notes or attach any file to use as agent knowledge.' : `Chat with an agent and ${meta ? meta.label.toLowerCase() : 'notes'} appear automatically, or add your own — Jev sorts them for you.`}</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filtered.map((i) => {
                const nm = NOTE_TYPES[i.type];
                const Ic = nm?.icon;
                return (
                <div key={i.memory_id} className="rounded-2xl border border-[hsl(var(--border))] bg-card p-4 group" data-testid={`memory-item-${i.memory_id}`}>
                  <div className="flex items-start gap-2">
                    {i.type === 'doc'
                      ? <FileText className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'hsl(var(--primary))' }} />
                      : <span className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0" style={{ background: nm?.color || 'hsl(var(--brand-mint))' }} />}
                    <div className="min-w-0 flex-1">
                      {i.title && <p className="font-600 text-sm truncate">{i.title}</p>}
                      {i.media_id && i.is_image && (
                        <a href={mediaSrc(i.media_id)} target="_blank" rel="noreferrer" className="block mt-1.5"><img src={mediaSrc(i.media_id)} alt={i.title} className="max-h-40 rounded-lg border border-[hsl(var(--border))] object-cover" /></a>
                      )}
                      {i.media_id && !i.is_image && (
                        <a href={mediaSrc(i.media_id)} target="_blank" rel="noreferrer" download={i.title} className="inline-flex items-center gap-1.5 mt-1.5 text-xs rounded-lg border border-[hsl(var(--border))] px-2 py-1 hover:bg-[hsl(var(--secondary))] transition-colors" data-testid={`memory-download-${i.memory_id}`}><Download className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} /> Download file</a>
                      )}
                      {!i.media_id && <p className="text-sm text-[hsl(var(--foreground))]/80 line-clamp-3">{i.content}</p>}
                      {i.media_id && !i.is_image && i.content && !i.content.startsWith('[Attached file') && <p className="text-sm text-[hsl(var(--foreground))]/80 line-clamp-3 mt-1">{i.content}</p>}
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {nm && (
                          <Badge variant="outline" className="text-[9px] gap-1" style={{ color: nm.color, borderColor: nm.color }} data-testid={`memory-type-badge-${i.memory_id}`}>
                            {Ic && <Ic className="h-2.5 w-2.5" />} {nm.single}
                          </Badge>
                        )}
                        {i.auto_tagged && (
                          <Badge variant="outline" className="text-[9px] gap-1 text-[hsl(var(--muted-foreground))]" title="Auto-sorted by Jev" data-testid={`memory-auto-badge-${i.memory_id}`}>
                            <Sparkles className="h-2.5 w-2.5" /> Auto
                          </Badge>
                        )}
                        {(i.tags || []).filter((t) => t !== 'auto').map((t) => <Badge key={t} variant="secondary" className="text-[9px]">{t}</Badge>)}
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(i)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid={`edit-memory-${i.memory_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(i.memory_id)} data-testid={`delete-memory-${i.memory_id}`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>
      </ScrollArea>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{dialog.item ? 'Edit memory' : 'Add memory'}</DialogTitle><DialogDescription>{dialog.item ? 'Pick the bucket this belongs in.' : 'Jev auto-sorts new notes into Facts, Preferences or Routines to keep your memory tidy.'}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {form.type === 'doc' ? (
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Document title" className="h-10 rounded-xl" data-testid="memory-title-input" />
            ) : (
              <div>
                <label className="text-xs font-600 text-[hsl(var(--muted-foreground))] mb-1.5 block">{dialog.item ? 'Bucket' : 'Starting bucket (Jev may re-sort)'}</label>
                <Select value={NOTE_TYPES[form.type] ? form.type : 'fact'} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="memory-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(NOTE_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`memory-type-option-${k}`}>{v.single}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={NOTE_TYPES[form.type] ? NOTE_TYPES[form.type].hint : 'Paste markdown notes here'} className="min-h-[120px] rounded-xl" data-testid="memory-content-input" />
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags, comma, separated" className="h-10 rounded-xl" data-testid="memory-tags-input" />
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} className="rounded-xl gap-1.5" style={{ background: 'hsl(var(--primary))' }} data-testid="save-memory-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{dialog.item ? 'Save' : 'Save & auto-sort'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
